import React, { useState } from 'react';
import Navbar from '../../components/Navbar';
import { adminAPI } from '../../services/api';

/**
 * Admin: Sales Export (XML). Downloads a per-order invoice breakdown — food subtotal,
 * food GST, platform fee + GST, delivery fee + GST, discount, roundOff and total paid,
 * plus a totals block. Values are the amounts AS BILLED (pricing snapshot). Default
 * scope = delivered orders by delivery date (IST); "All orders" = every non-cancelled
 * /failed order by order date. Downloaded through the authenticated API (CSRF + token),
 * then saved client-side as a .xml file.
 */
export default function SalesExport() {
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState(today);
  const [status, setStatus] = useState('delivered');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const download = async () => {
    setBusy(true); setMsg('');
    try {
      const params = { status };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await adminAPI.exportSales(params);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `foodwinc-sales-${status}-${startDate || 'all'}_${endDate || 'all'}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      setMsg('Downloaded ✓');
    } catch (e) {
      setMsg(e.response?.data?.message || 'Export failed');
    } finally { setBusy(false); }
  };

  const lbl = { fontSize: 13, color: '#374151', display: 'block' };
  const input = { padding: 8, border: '1px solid #d1d5db', borderRadius: 8, marginTop: 4 };

  return (
    <>
      <Navbar />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px 60px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>🧾 Sales Export (Excel)</h1>
        <p style={{ color: '#6b7280', marginBottom: 22 }}>
          Download per-order sales data as an Excel (.xlsx) file: order id, restaurant &amp; GST number, food subtotal, food GST,
          platform fee &amp; its GST, delivery fee &amp; its GST, discounts, and total paid — one row per order, plus a TOTALS row.
        </p>

        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <label style={lbl}>From (IST day)<br /><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={input} /></label>
            <label style={lbl}>To (IST day)<br /><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={input} /></label>
            <label style={lbl}>Scope<br />
              <select value={status} onChange={(e) => setStatus(e.target.value)} style={input}>
                <option value="delivered">Delivered (completed sales, by delivery date)</option>
                <option value="all">All orders (non-cancelled, by order date)</option>
              </select>
            </label>
          </div>

          <button
            onClick={download}
            disabled={busy}
            style={{ padding: '10px 20px', background: busy ? '#9ca3af' : '#111827', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}
          >
            {busy ? 'Preparing…' : '⬇ Download Excel'}
          </button>
          {msg && <span style={{ marginLeft: 14, fontSize: 13, color: msg.includes('✓') ? '#166534' : '#b91c1c' }}>{msg}</span>}

          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 16, lineHeight: 1.6 }}>
            Leave "From" empty for everything up to "To". Amounts are as billed (paise-accurate). Each order's components +
            roundOff reconcile to <b>total paid</b>. Dates use IST day boundaries. Large ranges may take a few seconds.
          </p>
        </div>
      </div>
    </>
  );
}
